import { useEffect, useState } from 'react';
import {
  APPROVAL_STAGE_LABEL,
  type ApprovalStage,
  type PortalUser,
  type StageApprovers as Approvers,
} from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { Icon } from './Icon.js';
import { useToast } from './Toast.js';

/**
 * Settings → Teams & approvals → Approvers: who may approve or decline each
 * stage. The API enforces it everywhere (website, Teams app, Teams cards,
 * Power Automate); this panel only edits the lists.
 */

const STAGES: { key: ApprovalStage; blurb: string }[] = [
  { key: 'cs', blurb: 'Trade applications, and stage 1 of credit applications' },
  { key: 'accounts', blurb: 'The credit decision (stage 2 of credit applications)' },
  { key: 'supplier', blurb: 'Supplier account applications' },
];

export function StageApprovers() {
  const toast = useToast();
  const [users, setUsers] = useState<PortalUser[] | null>(null);
  const [saved, setSaved] = useState<Approvers | null>(null);
  const [draft, setDraft] = useState<Approvers | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.listUsers(), api.getApprovers()])
      .then(([u, a]) => {
        setUsers(u.users.filter((x) => x.status === 'active').sort((p, q) => (p.name || p.email).localeCompare(q.name || q.email, 'en-GB')));
        setSaved(a.approvers);
        setDraft(a.approvers);
      })
      .catch(() => undefined);
  }, []);

  if (!users || !draft || !saved) {
    return <div className="card" style={{ padding: 'var(--pad-card)', color: 'var(--text-3)' }}>Loading approvers…</div>;
  }

  const toggle = (stage: ApprovalStage, id: string) =>
    setDraft({ ...draft, [stage]: draft[stage].includes(id) ? draft[stage].filter((x) => x !== id) : [...draft[stage], id] });
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.putApprovers(draft);
      setSaved(r.approvers);
      setDraft(r.approvers);
      toast({ title: 'Approvers saved', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: 'var(--pad-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon name="user" size={18} />
        <strong style={{ fontSize: 14.5 }}>Approvers</strong>
      </div>
      <p className="field-help" style={{ marginTop: 6 }}>
        Who can approve or decline each stage — in JFH Forms, in Teams, on Teams cards and through Power Automate. With
        nobody ticked, anyone with a JFH Forms user can decide that stage. To add someone here, first add them as a user
        on the Users page.
      </p>
      <div style={{ display: 'grid', gap: 14, marginTop: 12 }}>
        {STAGES.map((s) => (
          <fieldset key={s.key} style={{ border: '1px solid var(--border)', borderRadius: 9, padding: '10px 14px', margin: 0, minWidth: 0 }}>
            <legend style={{ fontWeight: 700, fontSize: 13.5, padding: '0 4px' }}>{APPROVAL_STAGE_LABEL[s.key]}</legend>
            <div className="field-help" style={{ marginTop: -2 }}>
              {s.blurb}. {draft[s.key].length ? `${draft[s.key].length} approver${draft[s.key].length === 1 ? '' : 's'}.` : 'Anyone with a JFH Forms user.'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginTop: 8 }}>
              {users.map((u) => (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={draft[s.key].includes(u.id)} onChange={() => toggle(s.key, u.id)} />
                  {u.name || u.email}
                  {u.name && <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{u.email}</span>}
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <button className="btn primary" onClick={() => void save()} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save approvers'}
        </button>
      </div>
    </div>
  );
}
